import { useCallback, useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router';
import equal from 'fast-deep-equal';

import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import { IKnmForm } from '../interfaces/IKnmForm';

interface IFormLeavingGuardProps<T extends IKnmFormRef | IDecisionFormRef> {
  formRef: T | undefined;
}

export const FormLeavingGuard = <T extends IKnmFormRef | IDecisionFormRef>({ formRef }: IFormLeavingGuardProps<T>) => {
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isUnapprovedViolations, setIsUnapprovedViolations] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [nextLocationPathname, setNextLocationPathname] = useState<string>();

  const closeModal = useCallback(() => setIsModalVisible(false), []);

  const showModal = (
    locationPathname: string,
    isFormValuesChanged: boolean,
    isFormWithUnapprovedViolations: boolean,
  ) => {
    setNextLocationPathname(locationPathname);
    isFormWithUnapprovedViolations && !isUnapprovedViolations && setIsUnapprovedViolations(true);
    isFormValuesChanged && !isFormChanged && setIsFormChanged(true);
    setIsModalVisible(true);
  };

  const isKnmForm = (ref: IKnmFormRef | IDecisionFormRef | undefined): ref is IKnmFormRef => {
    const formValues = ref?.current.getState().values as IKnmForm;

    return !!formValues?.resultDecisions;
  };

  const getIsFormChanged = useCallback(() => {
    const formInitialValues = formRef?.current.getState().initialValues;
    const formValues = formRef?.current.getState().values;

    const isValuesEqual = equal(formInitialValues, formValues);

    return !isValuesEqual;
  }, [formRef]);

  const getIsFormHasUnapprovedViolations = useCallback(() => {
    if (isKnmForm(formRef)) {
      const formValues = formRef?.current.getState().values;

      return !!formValues?.resultDecisions?.some(decision =>
        decision.violatedMandatoryRequirements?.some(requirement =>
          requirement.violatedMandatory.some(mandatory =>
            mandatory.violations?.some(
              violation =>
                (violation.status === ViolationStatusesEnum.APPROVING ||
                  violation.status === ViolationStatusesEnum.NOT_FIXED) &&
                violation.fixes?.some(fix => !fix.grade?.value),
            ),
          ),
        ),
      );
    }
  }, [formRef]);

  const handleBlockedNavigation = useCallback(
    ({ pathname }: { pathname: string }): boolean => {
      const isFormValuesChanged = getIsFormChanged();
      const isFormWithUnapprovedViolations = getIsFormHasUnapprovedViolations();

      if (isFormValuesChanged || isFormWithUnapprovedViolations) {
        showModal(pathname, isFormValuesChanged, isFormWithUnapprovedViolations ?? false);

        return false;
      }

      return true;
    },
    [getIsFormChanged],
  );

  const handleModalApply = useCallback(() => {
    closeModal();
    if (nextLocationPathname) {
      history.push(nextLocationPathname);
    }
  }, [closeModal, history, nextLocationPathname]);

  const handleUnload = useCallback(
    e => {
      const isFormValuesChanged = getIsFormChanged();

      if (isFormValuesChanged) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    [getIsFormChanged],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [handleUnload]);

  return (
    <>
      <Prompt when={!isModalVisible} message={handleBlockedNavigation} />
      <ConfirmModal
        visible={isModalVisible}
        title="Обновление страницы"
        text={`${
          isFormChanged ? 'Вы внесли изменения и не сохранили их, при обновлении страницы данные будут утеряны. ' : ''
        }${isUnapprovedViolations ? 'В паспорте присутствуют неоценённые нарушения. ' : ''}Желаете обновить страницу?`}
        applyButtonText="Да"
        cancelButtonText="Отмена"
        onApply={handleModalApply}
        onClose={closeModal}
      />
    </>
  );
};
